import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Col, Container, Row } from 'components'
import { useNavigate, useParams } from 'react-router-dom'
import { OutlineCheckIcon, UserPlusIcon } from 'assets'
import { abbreviateName, constants, formatPhone, getConnectUser } from 'helpers'
import {
    resetBulkEnrollmentAnalyticsSession,
    setBulkEnrollmentCurrentStep
} from 'helpers/bulkEnrollmentAnalytics'
import {
    composeBulkEnrollmentFullname,
    formatBulkEnrollmentPackagePrice
} from 'helpers/bulkEnrollmentHelpers'
import {
    useAppSelector,
    useBindDispatch,
    useBulkEnrollmentAnalytics,
    useScreenSize
} from 'hooks'
import './bulkEnrollment.scss'

export const BulkEnrollmentConfirmation = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { villageId } = useParams()
    const { veVillages = [] } = getConnectUser()
    const {
        trackCompleted,
        trackStarted,
        trackAbandoned,
        trackBatchSubmitted
    } = useBulkEnrollmentAnalytics(villageId)
    const {
        resetBulkEnrollmentHandler,
        initializeBulkEnrollmentHandler,
        ensureBulkEnrollmentVillageHandler,
        submitBulkEnrollmentHandler,
        clearBulkEnrollmentSubmitErrorHandler,
        navbarHandler
    } = useBindDispatch()
    const { rows, selectedPackage, submitting, submitError } = useAppSelector(
        (state) => state.bulkEnrollment
    )
    const committedRows = rows.filter((row) => row.committed)
    const screenSize = useScreenSize()
    const isMobile = screenSize.screenWidth < constants.MOBILE_SIZE

    const villageName = useMemo(() => {
        const village = veVillages.find(({ sf_id: sfId }) => sfId === villageId)
        return village?.name || ''
    }, [veVillages, villageId])

    useEffect(() => {
        setBulkEnrollmentCurrentStep('batch_validation')
    }, [])

    useEffect(() => {
        if (isMobile) {
            navbarHandler(constants.HIDE_PART_NAVBAR)
        } else {
            navbarHandler(constants.SHOW_NAVBAR)
        }
    }, [isMobile])

    const finishAndGoToVillage = async () => {
        if (!villageId || submitting) return

        clearBulkEnrollmentSubmitErrorHandler()
        const submitStartedAt = Date.now()
        // The bound thunk resolves to the dispatched action at runtime; the
        // binding helper types it as the plain action, hence the cast.
        const result = await (submitBulkEnrollmentHandler({
            villageId
        }) as unknown as Promise<{
            meta?: { requestStatus?: string }
            payload?: { rowCount?: number }
        }>)

        if (result?.meta?.requestStatus === 'fulfilled') {
            const rowCount = result.payload?.rowCount ?? rows.length
            trackBatchSubmitted(Date.now() - submitStartedAt, rowCount)
            trackCompleted(rowCount)
            resetBulkEnrollmentHandler()
            navigate(`/village/${villageId}`)
        }
    }

    useEffect(() => {
        if (villageId) ensureBulkEnrollmentVillageHandler(villageId)
    }, [villageId])

    const startNewLot = () => {
        trackAbandoned('batch_validation')
        resetBulkEnrollmentAnalyticsSession()
        trackStarted()
        initializeBulkEnrollmentHandler(villageId)
        navigate(`/village/${villageId}/bulk-enrollment/packages`)
    }

    return (
        <div className="bulk-enrollment-page bulk-enrollment-confirmation page-wrapper">
            <Container>
                <Row>
                    <Col md={12} lg={8}>
                        <div className="bulk-enrollment-success-hero">
                            <div className="bulk-enrollment-success-hero__icon">
                                <OutlineCheckIcon width="40" height="40" />
                            </div>
                            <h1 className="bulk-enrollment-success-hero__title">
                                {t('bulkEnrollment.lotSaved')}
                            </h1>
                            <p className="bulk-enrollment-success-hero__subtitle">
                                {villageName
                                    ? t(
                                          'bulkEnrollment.ordersCreatedSubtitleWithVillage',
                                          {
                                              count: committedRows.length,
                                              village: villageName
                                          }
                                      )
                                    : t(
                                          'bulkEnrollment.ordersCreatedSubtitle',
                                          { count: committedRows.length }
                                      )}
                            </p>
                        </div>

                        {selectedPackage && (
                            <div className="bulk-enrollment-package-chip bulk-enrollment-package-chip--readonly">
                                <span className="bulk-enrollment-package-chip__dot" />
                                <span className="bulk-enrollment-package-chip__name">
                                    {selectedPackage.name}
                                </span>
                                <span className="bulk-enrollment-package-chip__price bulk-enrollment-package-chip__price--end">
                                    {formatBulkEnrollmentPackagePrice(
                                        selectedPackage.price,
                                        selectedPackage.hidePrice
                                    )}
                                </span>
                            </div>
                        )}

                        <div className="bulk-enrollment-orders-label">
                            <p>{t('bulkEnrollment.ordersCreated')}</p>
                        </div>

                        <div className="bulk-enrollment-orders-list">
                            {committedRows.map((row) => {
                                const isExistingClient = !!row.matchedClient
                                const displayName =
                                    composeBulkEnrollmentFullname(
                                        row.firstname,
                                        row.lastname
                                    )

                                return (
                                    <div
                                        className="bulk-enrollment-order-row"
                                        key={row.id}
                                    >
                                        {isExistingClient ? (
                                            <div className="bulk-enrollment-order-row__avatar">
                                                <p className="bulk-enrollment-order-row__avatar_name">
                                                    {abbreviateName(
                                                        displayName
                                                    )}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="bulk-enrollment-order-row__avatar bulk-enrollment-order-row__avatar--new">
                                                <UserPlusIcon
                                                    color="#2E9E5C"
                                                    width="18"
                                                    height="18"
                                                />
                                            </div>
                                        )}
                                        <div className="bulk-enrollment-order-row__info">
                                            <div className="bulk-enrollment-order-row__name">
                                                <p className="bulk-enrollment-order-row__name">
                                                    {displayName}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="bulk-enrollment-order-row__phone">
                                                    +221{' '}
                                                    {formatPhone(row.phone)}
                                                </p>
                                            </div>
                                            {(row.selectedAddOns || []).length >
                                                0 && (
                                                <p className="bulk-enrollment-order-row__addons">
                                                    {(row.selectedAddOns || [])
                                                        .map((selection) => {
                                                            const addOn =
                                                                selectedPackage?.addOns?.find(
                                                                    (item) =>
                                                                        item.package_id ===
                                                                        selection.package_id
                                                                )
                                                            if (!addOn) {
                                                                return null
                                                            }
                                                            return `${addOn.product_name} ×${selection.quantity}`
                                                        })
                                                        .filter(Boolean)
                                                        .join(' · ')}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="bulk-enrollment-order-row__qty">
                                                ×{row.quantity}
                                            </p>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </Col>
                </Row>
            </Container>
            <div className="bulk-enrollment-footer bulk-enrollment-footer--confirmation">
                {submitError && (
                    <p className="bulk-enrollment-submit-error">
                        {t(submitError)}
                    </p>
                )}
                <Button
                    style="primary"
                    disable={submitting}
                    onClick={finishAndGoToVillage}
                    id="bulk-enrollment-finish"
                >
                    {submitting
                        ? t('bulkEnrollment.saving')
                        : t('bulkEnrollment.done')}
                </Button>
                <button
                    type="button"
                    className="bulk-enrollment-new-lot"
                    onClick={startNewLot}
                    id="bulk-enrollment-new-lot"
                    disabled={submitting}
                >
                    <p>{t('bulkEnrollment.newLot')}</p>
                </button>
            </div>
        </div>
    )
}
